import React, { useEffect, useState, useCallback } from "react"
import {
  Card, CardBody, Col, Row, Table, Button, Input, Label, FormGroup,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import Breadcrumbs from "../../../components/Common/Breadcrumb"
import { toastError, toastSuccess } from "../../../helpers/msg_helper"
import {
  searchVoucherTypes, createVoucherType, updateVoucherType, deleteVoucherType,
} from "../../../services/finance/voucherType"

const CATEGORIES = ["Contra", "Payment", "Receipt", "Journal", "Sales", "Purchase", "Debit Note", "Credit Note", "Inventory", "Other"]
const MODULES = [
  { v: "accounting", l: "Accounting" },
  { v: "inventory", l: "Inventory" },
  { v: "payroll", l: "Payroll" },
]

const emptyForm = {
  id: null, voucher_type: "", voucher_name: "", category: "Journal", module: "accounting",
  status: true, is_system: false,
  // numbering
  numbering_method: "automatic", prefix: "", no_of_digits: 5, fy_segment: true, yearly_reset: true,
  include_company_code: false, company_code_separator: "-",
  // behaviour
  is_optional_default: false, use_common_narration: true, narration_per_entry: false, print_after_save: false,
}

// Live preview of the generated number (sample company C001, finance year 2526, seq 1)
const previewNumber = f => {
  const num = "1".padStart(Number(f.no_of_digits) || 5, "0")
  const fy = f.fy_segment ? "2526" : ""
  const sep = f.company_code_separator || "-"
  const p = f.prefix || "PFX"
  if (f.numbering_method === "manual") return "Manual entry"
  if (f.numbering_method === "none") return "No number"
  if (f.include_company_code) return [p, "C001", fy, num].filter(Boolean).join(sep)
  return `${p}${fy}${num}`
}

const VoucherTypes = ({ embedded }) => {
  document.title = "Voucher Types | Everise"
  const [rows, setRows] = useState([])
  const [search, setSearch] = useState("")
  const [moduleFil, setModuleFil] = useState("")
  const [modal, setModal] = useState(false)
  const [form, setForm] = useState(emptyForm)
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const load = useCallback(async () => {
    try {
      const res = await searchVoucherTypes("all=1")
      setRows(res?.data || [])
    } catch (e) { toastError(e?.message || "Failed to load voucher types") }
  }, [])
  useEffect(() => { load() }, [load])

  const openNew = () => { setForm(emptyForm); setModal(true) }
  const openEdit = r => {
    const p = r.prefix || {}
    setForm({
      id: r.id, voucher_type: r.voucher_type, voucher_name: r.voucher_name || "", category: r.category || "Journal",
      module: r.module || "accounting", status: !!r.status, is_system: !!r.is_system,
      numbering_method: p.numbering_method || "automatic", prefix: p.prefix || "",
      no_of_digits: p.no_of_digits || 5, fy_segment: !!p.year, yearly_reset: !!p.yearly_reset,
      include_company_code: !!p.include_company_code, company_code_separator: p.company_code_separator || "-",
      is_optional_default: !!r.is_optional_default, use_common_narration: r.use_common_narration === undefined ? true : !!r.use_common_narration,
      narration_per_entry: !!r.narration_per_entry, print_after_save: !!r.print_after_save,
    })
    setModal(true)
  }

  const save = async () => {
    if (!form.voucher_type.trim()) return toastError("Name is required")
    try {
      const payload = { ...form }
      if (form.id) await updateVoucherType(form.id, payload)
      else await createVoucherType(payload)
      toastSuccess("Voucher type saved")
      setModal(false); load()
    } catch (e) { toastError(e?.message || (e?.errors && Object.values(e.errors)[0][0]) || "Save failed") }
  }

  const remove = async r => {
    if (!window.confirm(`Delete voucher type "${r.voucher_type}"?`)) return
    try { await deleteVoucherType(r.id); toastSuccess("Deleted"); load() }
    catch (e) { toastError(e?.message || "Delete failed") }
  }

  const filtered = rows.filter(r =>
    (!moduleFil || r.module === moduleFil) &&
    (!search || `${r.voucher_type} ${r.code}`.toLowerCase().includes(search.toLowerCase()))
  )

  return (
    <React.Fragment>
      <div className={embedded ? "" : "page-content tnt-scope"}>
        <div className={embedded ? "" : "container-fluid"}>
          {embedded ? (
            <div className="d-flex justify-content-end mb-2 app-tab-add">
              <Button color="primary" size="sm" onClick={openNew}>+ Voucher Type</Button>
            </div>
          ) : (
            <div className="d-flex justify-content-between align-items-center">
              <Breadcrumbs title="Administration" breadcrumbObject={[
                { title: "Administration", link: "#" },
                { title: "Voucher Types", link: "#" },
              ]} />
              <Button color="primary" size="sm" onClick={openNew}>+ Voucher Type</Button>
            </div>
          )}
          <Card className="me-2 px-2 pb-2 main-dashboard-bg"><CardBody>
            <Row className="mb-3">
              <Col md={3}>
                <Input type="select" value={moduleFil} onChange={e => setModuleFil(e.target.value)}>
                  <option value="">All modules</option>
                  {MODULES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </Input>
              </Col>
              <Col md={4}>
                <Input placeholder="Search name / code" value={search} onChange={e => setSearch(e.target.value)} />
              </Col>
            </Row>
            <Table responsive borderless className="align-middle tnt-table">
              <thead><tr>
                <th>Code</th><th>Name</th><th>Module</th><th>Base type</th>
                <th>Numbering</th><th>Narration</th><th>Active</th><th></th>
              </tr></thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}>
                    <td className="font-monospace small">{r.code}</td>
                    <td>{r.voucher_type}{r.is_system ? <span className="tnt-primary">standard</span> : null}</td>
                    <td className="text-capitalize">{r.module}</td>
                    <td>{r.category}</td>
                    <td className="font-monospace small">{previewNumber({
                      no_of_digits: r.prefix?.no_of_digits, fy_segment: r.prefix?.year, prefix: r.prefix?.prefix,
                      include_company_code: r.prefix?.include_company_code, company_code_separator: r.prefix?.company_code_separator,
                      numbering_method: r.prefix?.numbering_method,
                    })}</td>
                    <td className="small">{r.use_common_narration ? "Common" : ""}{r.use_common_narration && r.narration_per_entry ? " + " : ""}{r.narration_per_entry ? "Per entry" : ""}{!r.use_common_narration && !r.narration_per_entry ? "—" : ""}</td>
                    <td>{r.status ? <span className="badge bg-success">Active</span> : <span className="badge bg-secondary">Inactive</span>}</td>
                    <td className="text-nowrap">
                      <i className="uil-pen action-icon" role="button" title="Edit" onClick={() => openEdit(r)} />
                      {!r.is_system && <i className="uil-trash-alt action-icon danger" role="button" title="Delete" onClick={() => remove(r)} />}
                    </td>
                  </tr>
                ))}
                {!filtered.length && <tr><td colSpan="8" className="text-center text-muted">No voucher types</td></tr>}
              </tbody>
            </Table>
          </CardBody></Card>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} size="lg">
        <ModalHeader toggle={() => setModal(false)}>{form.id ? "Edit" : "New"} Voucher Type</ModalHeader>
        <ModalBody>
          {/* Identity */}
          <Row>
            <Col md={6}>
              <FormGroup className="mb-3">
                <Label>Name <span className="text-danger">*</span></Label>
                <Input value={form.voucher_type} onChange={e => set("voucher_type", e.target.value)} placeholder="e.g. Sales Invoice" />
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-3">
                <Label>Base type</Label>
                <Input type="select" value={form.category} onChange={e => set("category", e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Input>
              </FormGroup>
            </Col>
            <Col md={3}>
              <FormGroup className="mb-3">
                <Label>Module</Label>
                <Input type="select" value={form.module} onChange={e => set("module", e.target.value)}>
                  {MODULES.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}
                </Input>
              </FormGroup>
            </Col>
          </Row>

          <hr className="my-3" />
          <h6 className="mb-3" style={{ fontWeight: 600 }}>Numbering format</h6>
          {/* compact, content-sized fields (these values are short) that wrap as a multi-column row */}
          <div className="d-flex flex-wrap align-items-end" style={{ columnGap: "1.25rem", rowGap: ".75rem" }}>
            <div style={{ width: 150 }}>
              <Label className="mb-1 d-block">Method</Label>
              <Input type="select" value={form.numbering_method} onChange={e => set("numbering_method", e.target.value)}>
                <option value="automatic">Automatic</option>
                <option value="manual">Manual</option>
                <option value="none">None</option>
              </Input>
            </div>
            <div style={{ width: 120 }}>
              <Label className="mb-1 d-block">Prefix</Label>
              <Input value={form.prefix} disabled={form.numbering_method !== "automatic"} onChange={e => set("prefix", e.target.value)} />
            </div>
            <div style={{ width: 76 }}>
              <Label className="mb-1 d-block">Digits</Label>
              <Input type="number" min={1} max={12} value={form.no_of_digits} disabled={form.numbering_method !== "automatic"} onChange={e => set("no_of_digits", e.target.value)} />
            </div>
            <div style={{ width: 96 }}>
              <Label className="mb-1 d-block">Separator</Label>
              <Input value={form.company_code_separator} disabled={!form.include_company_code} onChange={e => set("company_code_separator", e.target.value)} />
            </div>
            <div>
              <Label className="mb-1 d-block">Preview</Label>
              <div className="font-monospace" title={previewNumber(form)}
                style={{ height: 38, display: "flex", alignItems: "center", background: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: 6, padding: "0 10px", fontSize: 13, color: "#374151", whiteSpace: "nowrap" }}>
                {previewNumber(form)}
              </div>
            </div>
          </div>
          <div className="d-flex flex-wrap mt-3" style={{ columnGap: "1.75rem", rowGap: ".5rem" }}>
            <FormGroup check className="mb-0">
              <Input id="fy" type="checkbox" checked={form.fy_segment} disabled={form.numbering_method !== "automatic"} onChange={e => set("fy_segment", e.target.checked)} />
              <Label check for="fy">Include finance year</Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input id="yr" type="checkbox" checked={form.yearly_reset} disabled={form.numbering_method !== "automatic"} onChange={e => set("yearly_reset", e.target.checked)} />
              <Label check for="yr">Yearly reset</Label>
            </FormGroup>
            <FormGroup check className="mb-0">
              <Input id="cc" type="checkbox" checked={form.include_company_code} disabled={form.numbering_method !== "automatic"} onChange={e => set("include_company_code", e.target.checked)} />
              <Label check for="cc">Include company code</Label>
            </FormGroup>
          </div>

          <hr className="my-3" />
          <h6 className="mb-3" style={{ fontWeight: 600 }}>Behaviour</h6>
          <div className="d-flex flex-wrap" style={{ columnGap: "1.75rem", rowGap: ".5rem" }}>
            <FormGroup check className="mb-0"><Input id="opt" type="checkbox" checked={form.is_optional_default} onChange={e => set("is_optional_default", e.target.checked)} /><Label check for="opt">Make optional as default</Label></FormGroup>
            <FormGroup check className="mb-0"><Input id="cn" type="checkbox" checked={form.use_common_narration} onChange={e => set("use_common_narration", e.target.checked)} /><Label check for="cn">Use common narration</Label></FormGroup>
            <FormGroup check className="mb-0"><Input id="pn" type="checkbox" checked={form.narration_per_entry} onChange={e => set("narration_per_entry", e.target.checked)} /><Label check for="pn">Narration for each entry</Label></FormGroup>
            <FormGroup check className="mb-0"><Input id="pr" type="checkbox" checked={form.print_after_save} onChange={e => set("print_after_save", e.target.checked)} /><Label check for="pr">Print after saving</Label></FormGroup>
            <FormGroup check className="mb-0"><Input id="ac" type="checkbox" checked={form.status} onChange={e => set("status", e.target.checked)} /><Label check for="ac">Active</Label></FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setModal(false)}>Cancel</Button>
          <Button color="primary" onClick={save}>Save</Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

export default VoucherTypes
